import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BaseQueryApi, FetchArgs } from "@reduxjs/toolkit/query";

import { toast } from "sonner";
import { setToken, setUser, setConfig, emptyTrial } from "@/state";
const customBaseQuery = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: any
) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: process.env.NEXT_PUBLIC_API_BASE_URL,
    credentials: "include",
    prepareHeaders: async (headers) => {
      return headers;
    },
  });

  try {
    const result: any = await baseQuery(args, api, extraOptions);
    if (result.error) {
      const skipToast = (
        args as FetchArgs & { meta?: { skipErrorToast?: boolean } }
      )?.meta?.skipErrorToast;

      const errorData = result.error.data;
      const errorMessage = errorData?.message
        ? errorData?.message
        : result?.error?.status === "FETCH_ERROR"
          ? "Internet Issues Detected"
          : result.error.status
            ? result.error.toString()
            : "An error occurred";
      // const errorMessage =
      //   errorData?.message ||
      //   result.error.status.toString() ||
      //   "An error occurred";
      !skipToast &&
        toast.error(`Error: ${errorMessage}`, {
          style: { background: "#fff", color: "red" },
          duration: 5000,
          closeButton: true,
          position: "top-right",
          cancel: true,
        });
    }

    const isMutationRequest =
      (args as FetchArgs).method && (args as FetchArgs).method !== "GET";
    const skipToast = (
      args as FetchArgs & { meta?: { skipErrorToast?: boolean } }
    )?.meta?.skipErrorToast;
    if (isMutationRequest && result.data.success && !skipToast) {
      const successMessage = result.data?.message;
      toast.success(successMessage, {
        style: { background: "#2da9df", color: "#fff" },
        duration: 5000,
        closeButton: true,
        position: "top-right",
        cancel: true,
      });
    }

    return result;
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";

    return {
      error: {
        status: "Internet issue or something broke up",
        error: errorMessage,
      },
    };
  }
};

interface SalesPersonDetails {
  name: string;
  role: string;
}

interface ProfessionalDetails {
  name: string;
  companyName: string;
  jobTitle: string;
}

interface UpdateUserRequest {
  email: string;
  salesPersonDetails: SalesPersonDetails;
  professionalDetails: ProfessionalDetails;
  role: string;
}

interface ContactUsRequest {
  message: string;
  reason: string;
  file?: File;
}

export const api = createApi({
  baseQuery: customBaseQuery,
  reducerPath: "api",
  tagTypes: ["Courses", "Messages", "User"],
  // tagTypes: ["Courses", "Users", "UserCourseProgress"],
  endpoints: (build) => ({
    getFeedback: build.query<any, any>({
      query: (id) => `messages/${id}`,
      // providesTags: (result, error, id) => [{ type: "Messages", id }],
    }),
    getAudioUrl: build.query<any, any>({
      query: (key) => ({
        url: "/audio-url",
        method: "POST",
        body: { key },
      }),
    }),

    register: build.mutation<any, { email: string; password: string }>({
      query: ({ email, password }) => ({
        url: "/register",
        method: "POST",
        body: { email, password },
        credentials: "include" as const,
      }),
      async onQueryStarted({ email, password }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          console.log(data, "yha set kro token baad mein data.activationToken");
          // dispatch(
          //   globalSlice.actions.setUser({
          //     email,
          //     role: "user",
          //     accessToken: data.accessToken,
          //   })
          // );
        } catch (error) {
          console.error("Registration failed:", error);
        }
      },
    }),
    activateAccount: build.query<any, any>({
      query: (id) => ({
        url: `activate-account?token=${id}`,
        method: "POST",
        body: { token: id },
      }),
      async onQueryStarted({ email, password }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            const user = data.user;
            dispatch(
              setUser({
                email: user.email,
                role: user.role,
                id: user.id,
                isVerified: user.isVerified,
                jobProfileRole: user.jobProfileRole,
                aboutYou: user.salesPersonDetails,
                aboutCompany: user.professionalDetails,
              })
            );

            dispatch(
              setToken({
                access: data.accessToken,
              })
            );
            window.location.href = "/role-selection";
          }
        } catch (error) {
          console.error("Login failed:", error);
        }
      },
    }),
    login: build.mutation<any, { email: string; password: string }>({
      query: ({ email, password }) => ({
        url: "/login",
        method: "POST",
        body: { email, password },
        credentials: "include" as const,
      }),
      async onQueryStarted({ email, password }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            const user = data.user;
            dispatch(
              setUser({
                email: user.email,
                role: user.role,
                id: user.id,
                isVerified: user.isVerified,
                jobProfileRole: user.jobProfileRole,
                aboutYou: user.salesPersonDetails,
                aboutCompany: user.professionalDetails,
              })
            );

            dispatch(
              setToken({
                access: data.accessToken,
              })
            );
          }
        } catch (error) {
          console.error("Login failed:", error);
        }
      },
    }),
    resetPassword: build.mutation<
      any,
      { activation_code: string; newPassword: string; activation_token: string }
    >({
      query: ({ activation_code, newPassword, activation_token }) => ({
        url: "/reset-password",
        method: "POST",
        body: { activation_code, newPassword, activation_token },
      }),
    }),
    reset: build.mutation<any, { email: string }>({
      query: ({ email }) => ({
        url: "/reset",
        method: "POST",
        body: { email },
      }),
    }),
    logout: build.mutation<any, {}>({
      query: () => ({
        url: "/logout",
        method: "POST",
      }),
    }),
    refreshToken: build.query({
      query: (data) => ({
        url: "/refresh",
        method: "GET",
        credentials: "include" as const,
        meta: { skipErrorToast: true },
      }),
    }),
    loadUser: build.query({
      query: (data) => ({
        url: "/me",
        method: "GET",
        credentials: "include" as const,
        meta: { skipErrorToast: true },
      }),
      providesTags: ["User"],
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.success) {
            const user = data?.data;
            dispatch(
              setUser({
                email: user?.email,
                role: user?.role,
                id: user?.id,
                isVerified: user?.isVerified,
                jobProfileRole: user?.jobProfileRole,
                aboutYou: user?.salesPersonDetails,
                aboutCompany: user?.professionalDetails,
                notifications: user?.notifications,
                subscription:
                  user?.subscriptions &&
                  Object.keys(user.subscriptions).length > 1 &&
                  !Object.keys(user.subscriptions).includes("assignedAt")
                    ? {
                        subscriptionId: user?.subscriptions.id,
                        stripeId: user?.subscriptions?.stripeId,
                        ownerId: user?.subscriptions?.ownerId,
                        planType: user?.subscriptions.planType,
                        seats: user?.subscriptions.seats,
                        startDate: user?.subscriptions?.startDate,
                        endDate: user?.subscriptions?.endDate,
                        duration: user?.subscriptions.duration,
                        paymentProvider: user?.subscriptions.paymentProvider,
                        isOwner: true,
                        status: "",
                        // isOwner:
                        //   user?.subscriptions && user?.subscriptions.teamMembers
                        //     ? user?.subscriptions.teamMembers?.isOwner
                        //     : "",
                      }
                    : user?.subscriptions &&
                        Object.keys(user.subscriptions).length > 1 &&
                        Object.keys(user.subscriptions).includes("assignedAt")
                      ? {
                          subscriptionId: user?.subscriptions.id,
                          stripeId: "",
                          ownerId: "",
                          planType: "TEAMUSER",
                          seats: 0,
                          startDate: user?.subscriptions?.startDate,
                          endDate: user?.subscriptions?.endDate,
                          duration: "",
                          isOwner: user?.subscriptions.isOwner,
                          status: user?.subscriptions.status,
                        }
                      : {
                          subscriptionId: "",
                          stripeId: "",
                          ownerId: "",
                          planType: "",
                          seats: 0,
                          startDate: "",
                          endDate: "",
                          duration: "",
                          isOwner: false,
                          status: "",
                        },
                trial: user?.trial ?? emptyTrial,
                expiredPlan: user?.expiredPlan ?? null,
              })
            );
            dispatch(
              setConfig({
                stripeEnabled: user?.stripeEnabled ?? true,
              })
            );
          }
        } catch (error: any) {
          if (
            error?.error?.data?.success === false ||
            error?.error?.status === 400
          ) {
            dispatch(
              setUser({
                // email: "",
                role: "",
                id: "",
                isVerified: false,
                jobProfileRole: "",
              })
            );
            dispatch(setToken({ access: "" }));
          }
        }
      },
    }),
    updateUser: build.mutation<any, UpdateUserRequest>({
      query: ({ email, salesPersonDetails, professionalDetails, role }) => ({
        url: "/update",
        method: "POST",
        body: { email, salesPersonDetails, professionalDetails, role },
      }),
    }),
    getPitches: build.query<
      any,
      {
        page?: number;
        limit?: number;
      }
    >({
      query: ({ page = 1, limit = 10 } = {}) => {
        const params = new URLSearchParams();
        if (page !== undefined) {
          params.set("page", page.toString());
        }
        if (limit !== undefined) {
          params.set("limit", limit.toString());
        }

        return {
          url: `get-pitches?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    getSession: build.query<any, string>({
      query: (sessionId) => ({
        url: "get-session",
        method: "POST",
        body: { sessionId },
        meta: { skipErrorToast: true },
      }),

      // providesTags: (result, error, id) => [{ type: "Messages", id }],
    }),

    // getTransactions: build.query<Transaction[], string>({
    //   query: (userId) => `transactions?userId=${userId}`,
    // }),
    createStripePaymentIntent: build.mutation<
      {
        success: boolean;
        message: string;
        data: { clientSecret: string };
      },
      { paymentId: string; seats: number }
    >({
      query: ({ paymentId, seats }) => ({
        url: `/stripe/payment-intent`,
        method: "POST",
        body: { paymentId, seats },
      }),
    }),
    createStripePaymentIntentForSeats: build.mutation<
      {
        success: boolean;
        message: string;
        data: any;
      },
      { seats: number }
    >({
      query: ({ seats }) => ({
        url: `/stripe/payment-intent/seats-only`,
        method: "POST",
        body: { seats },
      }),
      invalidatesTags: ["User"],
    }),
    createTransaction: build.mutation<
      Transaction,
      { stripeId: string; paymentId: string; seats: number }
    >({
      query: ({ stripeId, paymentId, seats }) => ({
        url: "transaction",
        method: "POST",
        body: { stripeId, paymentId, seats },
      }),
      invalidatesTags: ["User"],
    }),
    buySeats: build.mutation<
      Transaction,
      { stripeId: string; seats: number; amount: number }
    >({
      query: ({ stripeId, seats, amount }) => ({
        url: "buy-seats",
        method: "POST",
        body: { stripeId, seats, amount },
      }),
      invalidatesTags: ["User"],
    }),
    getTeamDashboard: build.query<
      any,
      {
        page?: number;
        limit?: number;
        search?: string;
        isActive?: boolean;
        isInactive?: boolean;
        isDeleted?: boolean;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: ({
        page,
        limit,
        search,
        isActive,
        isInactive,
        isDeleted,
        startDate,
        endDate,
      }) => {
        const rawParams: Record<string, any> = {
          page,
          limit,
          search: search?.trim() || undefined,
          isActive: isActive ? "true" : undefined,
          isInactive: isInactive ? "true" : undefined,
          isDeleted: isDeleted ? "true" : undefined,
          startDate: startDate ? new Date(startDate).toISOString() : undefined,
          endDate: endDate ? new Date(endDate).toISOString() : undefined,
        };
        const params = new URLSearchParams();

        Object.entries(rawParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.append(key, String(value));
          }
        });

        // if (page !== undefined) params.append("page", page.toString());
        // if (limit !== undefined) params.append("limit", limit.toString());
        // params.append("search", search ?? "");

        // if (isActive === true) params.append("isActive", String(isActive));
        // if (isInactive === true)
        //   params.append("isInactive", String(isInactive));
        // if (isDeleted === true) params.append("isDeleted", String(isDeleted));
        // if (startDate) params.append("startDate", startDate);
        // if (endDate) params.append("endDate", endDate);

        return {
          url: `/team-dashboard?${params.toString()}`,
          method: "GET",
        };
      },
    }),

    addTeamMembers: build.mutation<any, { emails: string[] }>({
      query: ({ emails }) => ({
        url: "/add-team-member",
        method: "POST",
        body: { emails },
      }),
      invalidatesTags: ["User"],
    }),
    updateTeamMemberStatus: build.mutation<any, { email: string }>({
      query: ({ email }) => ({
        url: "/update-team-member-status",
        method: "POST",
        body: { email },
      }),
    }),
    deleteTeamMember: build.mutation<any, { email: string }>({
      query: ({ email }) => ({
        url: "/delete-team-member",
        method: "POST",
        body: { email },
      }),
    }),
    getUserAnalytics: build.query<
      any,
      { userId: string; page?: number; limit?: number }
    >({
      query: ({ userId, page = 1, limit = 10 }) => {
        const params = new URLSearchParams();
        if (page !== undefined) {
          params.set("page", page.toString());
        }
        if (limit !== undefined) {
          params.set("limit", limit.toString());
        }
        if (userId !== undefined) {
          params.set("userId", userId.toString());
        }

        return {
          url: `get-analytics?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    paymentHistory: build.query<
      any,
      {
        page?: number;
        limit?: number;
      }
    >({
      query: ({ page = 1, limit = 10 } = {}) => {
        const params = new URLSearchParams();
        if (page !== undefined) {
          params.set("page", page.toString());
        }
        if (limit !== undefined) {
          params.set("limit", limit.toString());
        }

        return {
          url: `payment-history?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    contactUs: build.mutation<
      { success: boolean; message: string },
      ContactUsRequest
    >({
      query: ({ message, reason, file }) => {
        const formData = new FormData();
        formData.append("message", message);
        formData.append("reason", reason);
        if (file) {
          formData.append("file", file);
        }

        return {
          url: "/contact-us",
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
    }),
    updateProfile: build.mutation<any, { password: string; name: string }>({
      query: ({ password, name }) => ({
        url: "/update-profile",
        method: "POST",
        body: { password, name },
      }),
      invalidatesTags: ["User"],
    }),
    createCheckoutSession: build.mutation<
      {
        success: boolean;
        message: string;
        data: any;
      },
      { paymentId: string; seats: number }
    >({
      query: ({ paymentId, seats }) => ({
        url: `/create-checkout-session`,
        method: "POST",
        body: { paymentId, seats },
      }),
    }),
    completeCheckoutSession: build.mutation<
      {
        success: boolean;
        message: string;
        data: {
          paymentStatus: string;
          completed: boolean;
          subscription?: any;
        };
      },
      { sessionId: string }
    >({
      query: ({ sessionId }) => ({
        url: "/complete-checkout-session",
        method: "POST",
        body: { sessionId },
      }),
      invalidatesTags: ["User"],
    }),

    getNotifications: build.query<
      any,
      {
        page?: number;
        limit?: number;
        isRead?: boolean;
      }
    >({
      query: ({ page = 1, limit = 10, isRead } = {}) => {
        const params = new URLSearchParams();
        if (page !== undefined) {
          params.set("page", page.toString());
        }
        if (limit !== undefined) {
          params.set("limit", limit.toString());
        }
        if (isRead !== undefined) {
          params.set("isRead", isRead.toString());
        }

        return {
          url: `notifications?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    unReadCount: build.query<any, void>({
      query: () => ({
        url: "notifications/unread-count",
        method: "GET",
      }),
    }),

    readAll: build.query<any, void>({
      query: () => ({
        url: "notifications/read-all",
        method: "PATCH",
      }),
    }),
    markNotificationAsRead: build.mutation<any, { notificationId: string }>({
      query: ({ notificationId }) => ({
        url: "/notifications/read",
        method: "POST",
        body: { notificationId },
      }),
    }),
    deleteNotification: build.mutation<any, { notificationId: string }>({
      query: ({ notificationId }) => ({
        url: "/notifications/delete",
        method: "POST",
        body: { notificationId },
      }),
    }),
    cancelSubscription: build.mutation<
      { success: boolean; message: string },
      void
    >({
      query: () => ({
        url: "/cancel-subscription", // backend route
        method: "GET",
      }),
      invalidatesTags: ["User"],
    }),
    startFreeTrial: build.mutation<
      {
        success: boolean;
        message: string;
        data: { activated: boolean; subscription: any };
      },
      { planType: "USER" | "TEAM"; seats?: number }
    >({
      query: (body) => ({
        url: "/start-trial",
        method: "POST",
        body,
      }),
      invalidatesTags: ["User"],
    }),
    createPortalSession: build.mutation<
      { success: boolean; message: string; data: { url: string } },
      void
    >({
      query: () => ({
        url: "/create-portal-session",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetFeedbackQuery,
  useGetAudioUrlQuery,
  useGetSessionQuery,
  useGetPitchesQuery,
  useRegisterMutation,
  useActivateAccountQuery,
  useLoginMutation,
  useResetPasswordMutation,
  useResetMutation,
  useLogoutMutation,
  useRefreshTokenQuery,
  useLoadUserQuery,
  useUpdateUserMutation,
  //  useGetTransactionsQuery,
  useCreateTransactionMutation,
  useBuySeatsMutation,
  useCreateStripePaymentIntentMutation,
  useCreateStripePaymentIntentForSeatsMutation,
  useGetTeamDashboardQuery,
  useAddTeamMembersMutation,
  useUpdateTeamMemberStatusMutation,
  useDeleteTeamMemberMutation,
  useGetUserAnalyticsQuery,
  usePaymentHistoryQuery,
  useContactUsMutation,
  useUpdateProfileMutation,
  useCreateCheckoutSessionMutation,
  useCompleteCheckoutSessionMutation,
  useStartFreeTrialMutation,
  useCreatePortalSessionMutation,
  useGetNotificationsQuery,
  useUnReadCountQuery,
  useMarkNotificationAsReadMutation,
  useDeleteNotificationMutation,
  useReadAllQuery,
  useCancelSubscriptionMutation,
} = api;
